import { Avatar, Button, InlineAlert, Tag, TextField } from '@lego/connect-components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssignOperatorSearch } from './assign-operator-search';
import type { GroupSelection, KnownOperator, OperatorAccessEntry } from './types';
import { initials, operatorDisplayName } from './utils';

export const GroupDetailPanel = ({
  selection,
  members,
  assignableOperators,
  isBusy,
  onRenameGroup,
  onDeleteGroup,
  onRemoveMember,
  onAssignKnownOperator,
  onInviteAndAssign,
}: {
  selection: GroupSelection;
  members: OperatorAccessEntry[];
  assignableOperators: KnownOperator[];
  isBusy: boolean;
  onRenameGroup: (target: string) => Promise<boolean>;
  onDeleteGroup: () => void;
  onRemoveMember: (entry: OperatorAccessEntry) => void;
  onAssignKnownOperator: (id: number) => Promise<boolean>;
  onInviteAndAssign: (email: string) => Promise<boolean>;
}) => {
  const { t } = useTranslation();
  const [renameValue, setRenameValue] = useState<string | null>(null);
  const rename = async () => {
    if (renameValue !== null && (await onRenameGroup(renameValue))) setRenameValue(null);
  };

  return (
    <>
      <div className="px-300 py-200 border-b border-core-slate-100 bg-core-slate-50 flex items-center justify-between gap-200">
        <div className="min-w-0 flex-1">
          <div className="text-body-xs text-core-slate-900 font-medium">
            {selection.application}
          </div>
          {renameValue !== null ? (
            <div className="flex items-center gap-100 mt-25">
              <TextField
                size="medium"
                value={renameValue}
                onChange={(_e, value) => setRenameValue(value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void rename();
                  if (e.key === 'Escape') setRenameValue(null);
                }}
              />
              <Button
                variant="primary"
                size="small"
                onClick={() => void rename()}
                disabled={isBusy || !renameValue.trim()}
              >
                {t('operatorAccessPage.action.save')}
              </Button>
              <Button
                variant="neutral"
                size="small"
                onClick={() => setRenameValue(null)}
                disabled={isBusy}
              >
                {t('operatorAccessPage.action.cancel')}
              </Button>
            </div>
          ) : (
            <div className="font-bold text-core-slate-1300 text-body-md truncate">
              {selection.groupName}
            </div>
          )}
        </div>
        {renameValue === null && (
          <div className="flex items-center gap-100">
            <Button
              variant="neutral"
              size="small"
              onClick={() => setRenameValue(selection.groupName)}
              disabled={isBusy}
            >
              {t('operatorAccessPage.action.rename')}
            </Button>
            <Button variant="negative" size="small" onClick={onDeleteGroup} disabled={isBusy}>
              {t('operatorAccessPage.action.deleteGroup')}
            </Button>
          </div>
        )}
      </div>
      <div className="p-300 space-y-300 overflow-y-auto flex-1">
        <InlineAlert variant="informative">
          <InlineAlert.Content>
            {t('operatorAccessPage.display.groupAccessExplanation')}
          </InlineAlert.Content>
        </InlineAlert>
        <div>
          <div className="flex items-center gap-100 mb-100">
            <h3 className="text-body-sm font-bold text-core-slate-1100">
              {t('operatorAccessPage.display.members')}
            </h3>
            <Tag
              variant="neutral"
              size="small"
              aria-label={t('operatorAccessPage.display.memberCountTooltip', {
                count: members.length,
              })}
            >
              {String(members.length)}
            </Tag>
          </div>
          {members.length === 0 ? (
            <div className="border border-dashed border-core-slate-100 rounded-lg p-300 text-center text-body-xs text-core-slate-900">
              {t('operatorAccessPage.display.noMembers')}
            </div>
          ) : (
            <div className="border border-core-slate-100 rounded-lg divide-y divide-core-slate-100 min-w-fit">
              {members.map((member) => {
                const displayName = operatorDisplayName(member);
                return (
                  <div
                    key={member.id}
                    data-testid="member-row"
                    className="flex items-center justify-between px-200 py-150"
                  >
                    <div className="flex items-center gap-200">
                      <Avatar
                        type="initials"
                        name={displayName}
                        initials={member.oid ? initials(displayName) : '?'}
                        size="medium"
                      />
                      <div>
                        <div className="text-body-xs font-semibold flex flex-col items-baseline sm:flex-row gap-100">
                          {displayName}
                          {!member.oid && (
                            <Tag variant="informative" size="small">
                              {t('operatorAccessPage.display.invitedTag')}
                            </Tag>
                          )}
                        </div>
                        {member.email && member.displayName && (
                          <div className="text-body-xs text-core-slate-900">{member.email}</div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="negative"
                      appearance="ghost"
                      size="small"
                      onClick={() => onRemoveMember(member)}
                      disabled={isBusy}
                    >
                      {t('operatorAccessPage.action.remove')}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <AssignOperatorSearch
          assignableOperators={assignableOperators}
          isBusy={isBusy}
          onAssignKnownOperator={onAssignKnownOperator}
          onInviteAndAssign={onInviteAndAssign}
        />
      </div>
    </>
  );
};
